import { useState, useEffect, useRef } from "react";
import {
  Users,
  Search,
  Filter,
  Shield,
  Mail,
  Phone,
  Calendar,
  Edit2,
  X,
  Save,
  CheckCircle,
  XCircle,
  UserCog,
  Plus,
  Trash2,
  Lock,
  BarChart3,
  Upload,
  Settings,
  UserPlus,
  TrendingUp,
  User as UserIcon,
  ChevronDown,
} from "lucide-react";
import { COUNTRY_CODES } from "../../constants/countryCodes";
import BaseApi from "../../services/base-api";
import { User } from "../../types/User";
import { Role } from "../../types/Role";
import { useToast } from "../../context/ToastContext";
import ContactInfo from "../../components/ContactInfo";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../../components/Modals/Confirm";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { Divider, Tooltip } from "@mui/material";
import LoadingComponent from "../../components/LoadingComponent";
import Pagination from "../../components/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

interface Permission {
  id: string;
  name: string;
  description: string;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  usersByRole: { role: string; count: number }[];
  recentSignups: number;
}

interface UserPreferences {
  language: string;
  theme: string;
  notifications: boolean;
  emailUpdates: boolean;
}

// Same default as the sign-in screen
const DEFAULT_PHONE_CODE = "+974";

const UsersManagementPage = () => {
  const { success, err } = useToast();
  const { t } = useTranslation();

  const { permissions } = useAuth();
  // Check if user has permission to create or edit products
  const canCreate = hasPermission(permissions, PERMISSIONS.CREATE_USERS);
  const canEdit = hasPermission(permissions, PERMISSIONS.EDIT_USERS);
  const canDelete = hasPermission(permissions, PERMISSIONS.DELETE_USERS);

  const [apiTotalItems, setApiTotalItems] = useState(0); // ADD THIS
  const [sortBy, setSortBy] = useState<"createdAt" | "firstName">("createdAt"); // ADD THIS
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc"); // ADD THIS

  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [filtered, setFiltered] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim());
  const latestRequestRef = useRef(0);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"table" | "cards">("cards");

  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [permissionsModal, setPermissionsModal] = useState(false);
  const [preferencesModal, setPreferencesModal] = useState(false);
  const [statsModal, setStatsModal] = useState(false);
  const [bulkUpdateModal, setBulkUpdateModal] = useState(false);
  const [profileModal, setProfileModal] = useState(false);

  // Current data
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [currentProfile, setCurrentProfile] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [customPermissions, setCustomPermissions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const normalizeRoleName = (role?: string | null) =>
    String(role || "")
      .trim()
      .toLowerCase();

  const getMatchingRole = (
    userRoleId?: string | null,
    userRoleName?: string | null,
  ) => {
    if (userRoleId) {
      const roleById = roles.find((role) => role.id === userRoleId);
      if (roleById) return roleById;
    }

    const normalizedRoleName = normalizeRoleName(userRoleName);
    if (!normalizedRoleName) return null;

    return (
      roles.find((role) => normalizeRoleName(role.name) === normalizedRoleName) ||
      null
    );
  };

  const openEditUserModal = (user: User) => {
    const matchedRole = getMatchingRole(user.roleId, user.role);

    setEditingUser(
      matchedRole
        ? {
            ...user,
            roleId: matchedRole.id,
            role: matchedRole.name,
          }
        : user,
    );
    setEditModal(true);
  };

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: t("Confirm"),
    cancelText: t("Cancel"),
    onConfirm: () => {},
  });

  // Form state
  const [createFormData, setCreateFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneCode: DEFAULT_PHONE_CODE,
    role: "",
    isActive: true,
  });
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [searchCountry, setSearchCountry] = useState("");

  const [preferencesData, setPreferencesData] = useState<UserPreferences>({
    language: "ar",
    theme: "light",
    notifications: true,
    emailUpdates: true,
  });

  const [bulkUpdateData, setBulkUpdateData] = useState({
    role: "",
    isActive: true,
  });

  const editingUserMatchedRole = editingUser
    ? getMatchingRole(editingUser.roleId, editingUser.role)
    : null;

  useEffect(() => {
    fetchRoles();
    fetchCurrentProfile();

    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setViewMode("cards");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    filterUsers();
    setCurrentPage(1);
  }, [debouncedSearch, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearch,
    roleFilter,
    statusFilter,
    sortBy,
    sortOrder,
  ]);

  // API Calls
  const fetchUsers = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (roleFilter !== "all") {
        params.append("role", roleFilter);
      }
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }

      const res = await BaseApi.get(`/users?${params.toString()}`);
      if (requestId !== latestRequestRef.current) return;
      if (res.success) {
        setUsers(res.data || []);
        setFiltered(res.data || []);
        setApiTotalItems(res.pagination?.totalItems || res.data.length);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Error fetching users:", error);
      err(t("Failed to load users"));
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await BaseApi.get("/roles");
      if (res.success) {
        setRoles(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const fetchCurrentProfile = async () => {
    try {
      const res = await BaseApi.get("/users/profile");
      if (res.success) {
        setCurrentProfile(res.data);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const fetchUserStats = async () => {
    try {
      const res = await BaseApi.get("/users/stats");
      if (res.success) {
        // console.log('res - ', res)
        setUserStats(res.data);
        setStatsModal(true);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
      err("Failed to fetch user statistics");
    }
  };

  const fetchUsersByRole = async (roleId: string) => {
    try {
      const res = await BaseApi.get(`/users/role/${roleId}`);
      if (res.success) {
        setUsers(res.data);
      }
    } catch (error) {
      console.error("Error fetching users by role:", error);
      err("Failed to fetch users by role");
    }
  };

  const handleCreateUser = async () => {
    if (
      !createFormData.firstName ||
      !createFormData.lastName ||
      !createFormData.phone.trim()
    ) {
      err("Please fill in all required fields");
      return;
    }

    try {
      setSaving(true);
      const res = await BaseApi.post("/users", {
        ...createFormData,
        email: createFormData.email.trim() || undefined,
        phone: createFormData.phone.trim(),
        // API stores the dial code without "+", same as sign in
        phoneCode: createFormData.phoneCode.replace("+", ""),
      });
      if (res.success) {
        success("User created successfully!");
        fetchUsers();
        setCreateModal(false);
        resetCreateForm();
      } else {
        err(res.message || "Failed to create user");
      }
    } catch (error) {
      console.error("Error creating user:", error);
      err(error instanceof Error ? error.message : "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  const getFilteredUsers = (userList: User[]) => {
    let result = [...userList];

    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.phone?.includes(debouncedSearch),
      );
    }

    if (roleFilter !== "all") {
      result = result.filter((user) => user.role === roleFilter);
    }

    if (statusFilter !== "all") {
      result = result.filter((user) =>
        statusFilter === "active" ? user.isActive : !user.isActive,
      );
    }

    return result;
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    try {
      setSaving(true);
      const selectedRole = roles.find((role) => role.id === editingUser.roleId);
      const updatedUser = {
        ...editingUser,
        role: selectedRole?.name || editingUser.role,
        roleId: selectedRole?.id || editingUser.roleId,
      };

      const res = await BaseApi.put(`/users/${editingUser.id}`, {
        role: updatedUser.role,
        roleId: updatedUser.roleId,
        isActive: updatedUser.isActive,
      });

      if (res.success) {
        const updatedUsers = users.map((u) =>
          u.id === editingUser.id ? updatedUser : u,
        );
        setUsers(updatedUsers);
        setFiltered(getFilteredUsers(updatedUsers));
        setEditModal(false);
        setEditingUser(null);
        success("User updated successfully!");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      err("Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!currentProfile) return;

    try {
      setSaving(true);
      const res = await BaseApi.put("/users/profile", currentProfile);
      if (res.success) {
        success("Profile updated successfully!");
        setProfileModal(false);
        fetchCurrentProfile();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      err("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePreferences = async () => {
    try {
      setSaving(true);
      const res = await BaseApi.patch(
        "/users/profile/preferences",
        preferencesData,
      );
      if (res.success) {
        success("Preferences updated successfully!");
        setPreferencesModal(false);
      }
    } catch (error) {
      console.error("Error updating preferences:", error);
      err("Failed to update preferences");
    } finally {
      setSaving(false);
    }
  };

  const openConfirmModal = (config: {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
  }) => {
    setConfirmConfig({
      isOpen: true,
      title: config.title ?? t("Confirm Action"),
      message: config.message ?? t("Are you sure?"),
      confirmText: config.confirmText ?? "Confirm",
      cancelText: config.cancelText ?? "Cancel",

      onConfirm: () => {
        config.onConfirm();
        closeConfirmModal();
      },
    });
  };

  const closeConfirmModal = () => {
    setConfirmConfig((c) => ({ ...c, isOpen: false }));
  };

  const handleDeleteUser = async (user: User) => {
    openConfirmModal({
      title: t("Remove User"),
      message: t(
        `Are you sure you want to remove ${user.firstName + " " + user.lastName}?`,
      ),
      confirmText: t("Remove"),
      onConfirm: async () => {
        try {
          setSaving(true);
          const res = await BaseApi.delete(`/users/${user.id}`);
          if (res.success) {
            success("User deleted successfully!");
            fetchUsers();
            setDeleteModal(false);
          }
        } catch (error) {
          console.error("Error deleting user:", error);
          err("Failed to delete user");
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleToggleStatus = async (user: User) => {
    try {
      const res = await BaseApi.patch(`/users/${user.id}/toggle-active`, {});
      if (res.success) {
        setUsers(
          users.map((u) =>
            u.id === user.id ? { ...u, isActive: !u.isActive } : u,
          ),
        );
        success(
          `User ${!user.isActive ? "activated" : "deactivated"} successfully!`,
        );
      }
    } catch (error) {
      console.error("Error toggling user status:", error);
      err("Failed to update user status");
    }
  };

  const handleUpdateCustomPermissions = async () => {
    if (!editingUser) return;

    try {
      setSaving(true);
      const res = await BaseApi.patch(`/users/${editingUser.id}/permissions`, {
        permissions: customPermissions,
      });
      if (res.success) {
        success("Permissions updated successfully!");
        setPermissionsModal(false);
        setEditingUser(null);
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
      err("Failed to update permissions");
    } finally {
      setSaving(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedUsers.length === 0) {
      err("Please select users to update");
      return;
    }

    try {
      setSaving(true);
      const res = await BaseApi.patch("/users/bulk-update", {
        userIds: selectedUsers,
        updates: bulkUpdateData,
      });
      if (res.success) {
        success(`${selectedUsers.length} users updated successfully!`);
        fetchUsers();
        setBulkUpdateModal(false);
        setSelectedUsers([]);
      }
    } catch (error) {
      console.error("Error bulk updating users:", error);
      err("Failed to bulk update users");
    } finally {
      setSaving(false);
    }
  };

  const filterUsers = () => {
    setFiltered(getFilteredUsers(users));
  };

  const resetCreateForm = () => {
    setCreateFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      phoneCode: DEFAULT_PHONE_CODE,
      role: "",
      isActive: true,
    });
    setShowCountryDropdown(false);
    setSearchCountry("");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === filtered.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filtered.map((u) => u.id));
    }
  };

  if (initialLoading) {
    return <LoadingComponent title={t("Users")} />;
  }

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // const showVerification = (isVerified: boolean) => {
  //   return (
  //     <>
  //       {isVerified ? (
  //         <CheckCircle
  //           className="mt-1 text-green-700 font-semibold"
  //           size={15}
  //         />
  //       ) : (
  //         <XCircle className="mt-1 text-red-700 font-semibold" size={15} />
  //       )}
  //     </>
  //   );
  // };

  const finalTotalItems = apiTotalItems;
  const finalTotalPages = Math.ceil(finalTotalItems / itemsPerPage);
  const currentPageData = users;

  return (
    <div className="min-h-screen bg-[#f2e6db] py-4 sm:py-8 px-4">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div className="mx-auto">
        <div
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
          style={{ animation: "fadeIn 0.5s" }}
        >
          {/* Header */}
          <div className="bg-white px-4 sm:px-8 py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="relative bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                  <Users className="w-8 h-8 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-black">
                    {t("Users")}
                  </h1>
                  <p className="text-gray-500 text-sm mt-1">
                    {t("Manage users and profiles")} • {finalTotalItems || filtered.length}{" "}
                    {t("users")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setProfileModal(true)}
                  className="px-3 sm:px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-semibold text-sm flex items-center gap-2"
                >
                  <UserCog size={16} />
                  <span className="hidden sm:inline">{t("My Profile")}</span>
                </button>
                <button
                  onClick={() => fetchUserStats()}
                  className="px-3 sm:px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-semibold text-sm flex items-center gap-2"
                >
                  <BarChart3 size={16} />
                  <span className="hidden sm:inline">{t("Statistics")}</span>
                </button>

                <Tooltip
                  title={
                    canCreate
                      ? t("Add User")
                      : t("You don't have permission to add user")
                  }
                  placement="top"
                >
                  <button
                    disabled={!canCreate}
                    onClick={() => setCreateModal(true)}
                    className="px-3 sm:px-4 border border-gray-400 py-2 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors font-semibold text-sm flex items-center gap-2"
                  >
                    <Plus size={16} />
                    {t("New User")}
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Filters and Bulk Actions */}
          <div className="p-4 sm:p-6 border-b border-gray-200 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t("Search users...")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm"
                />
              </div>

              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none appearance-none text-sm"
                >
                  <option value="all">{t("All Roles")}</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none appearance-none text-sm"
                >
                  <option value="all">{t("All Status")}</option>
                  <option value="active">{t("Active")}</option>
                  <option value="inactive">{t("Inactive")}</option>
                </select>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode("cards")}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors ${
                    viewMode === "cards"
                      ? "bg-black text-white"
                      : "bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {t("Cards")}
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors ${
                    viewMode === "table"
                      ? "bg-black text-white"
                      : "bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {t("Table")}
                </button>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedUsers.length > 0 && (
              <div className="bg-blue-100 border-2 border-blue-200 rounded-lg p-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-blue-900">
                  {selectedUsers.length} {t("users selected")}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setBulkUpdateModal(true)}
                    className="px-3 py-1.5 bg-[#0d223f] text-white rounded-lg hover:bg-[#0d223f] text-sm font-semibold flex items-center gap-1"
                  >
                    <Upload size={14} />
                    {t("Bulk Update")}
                  </button>
                  <button
                    onClick={() => setSelectedUsers([])}
                    className="px-3 py-1.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-semibold"
                  >
                    {t("Clear")}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div
            className={
              "p-4 sm:p-6 bg-gray-50" +
              (loading ? " opacity-50 pointer-events-none transition-opacity" : "")
            }
          >
            {filtered.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">{t("No users found")}</p>
              </div>
            ) : viewMode === "cards" ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {filtered.map((user, index) => (
                    <div
                      key={user.id}
                      className="bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all hover:border-gray-300"
                      style={{
                        animation: `fadeIn 0.5s ease-out ${index * 0.1}s both`,
                      }}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            className="w-5 h-5 rounded border-gray-300 text-blue-600"
                          />
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                            {user.profileImage ? (
                              <img
                                src={user.profileImage}
                                alt={user.id}
                                className="w-full h-full object-cover"
                              />
                            ) : user.firstName || user.lastName ? (
                              <div>
                                {user.firstName?.charAt(0)}
                                {user.lastName?.charAt(0)}
                              </div>
                            ) : (
                              <UserIcon size={22} />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 truncate">
                              {user.firstName || user.lastName ? (
                                <div>
                                  {user.firstName} {user.lastName}
                                </div>
                              ) : (
                                "No Name"
                              )}
                            </div>
                            <div
                              className={`inline-flex font-bold items-center gap-1 px-3 py-1 rounded-full text-md font-semibold ${user.role === "admin" ? "bg-green-100 text-green-800" : "bg-purple-100 text-purple-800"}`}
                            >
                              {/* <div className="text-md text-gray-600 flex items-center gap-1"> */}
                              <Shield size={18} />
                              {user.role}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {
                          <div className="flex flex-row gap-1">
                            <ContactInfo
                              type="email"
                              value={user.email || "-"}
                              icon={<Mail size={14} />}
                            />
                            {/* {!!user.email &&
                              showVerification(user.isEmailVerified)} */}
                          </div>
                        }
                        {
                          <div className="flex flex-row gap-1">
                            <ContactInfo
                              type="phone"
                              value={
                                (user?.phoneCode || "") + (user.phone || "-")
                              }
                              icon={<Phone size={14} />}
                            />
                            {/* {!!user.phone &&
                              showVerification(user.isPhoneVerified)} */}
                          </div>
                        }
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <button
                          onClick={() => handleToggleStatus(user)}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold transition-colors ${
                            user.isActive
                              ? "bg-green-100 text-green-800 hover:bg-green-200"
                              : "bg-red-100 text-red-800 hover:bg-red-200"
                          }`}
                        >
                          {user.isActive ? (
                            <>
                              <CheckCircle size={14} />
                              {t("Active")}
                            </>
                          ) : (
                            <>
                              <XCircle size={14} />
                              {t("Inactive")}
                            </>
                          )}
                        </button>
                        <span className="text-xs text-gray-500">
                          {formatDate(user.createdAt)}
                        </span>
                      </div>
                      <div className="w-full flex-col items-center mb-4 card text-sm text-gray-500">
                        {/* <!-- Language --> */}
                        <div className="flex flex-row items-center justify-between  my-1">
                          <label className="block text-gray-700 mb-2">
                            {t("Language")}
                          </label>
                          <select
                            id="language"
                            value={user.preferences.language}
                            disabled
                            onChange={() => {}}
                          >
                            <option value="en">English</option>
                            <option value="ar">Arabic</option>
                          </select>
                        </div>

                        {/* <!-- Notifications --> */}
                        {/* <div className="flex flex-row items-center justify-between my-1">
                        <label className="block text-gray-700 mb-2">SMS Notifications</label>
                        <input type="checkbox" id="sms" checked={user.preferences.notifications.sms} disabled onChange={() => {}}/>
                      </div> */}

                        <div className="flex flex-row items-center justify-between my-1">
                          <label className="block text-gray-700 mb-2">
                            {t("Email Notifications")}
                          </label>
                          <input
                            type="checkbox"
                            id="email"
                            checked={user.preferences.notifications.email}
                            disabled
                            onChange={() => {}}
                          />
                        </div>

                        <div className="flex flex-row items-center justify-between my-1">
                          <label className="block text-gray-700 mb-2">
                            {t("WhatsApp Notifications")}
                          </label>
                          <input
                            type="checkbox"
                            id="whatsapp"
                            checked={user.preferences.notifications.whatsapp}
                            disabled
                            onChange={() => {}}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <Tooltip
                          title={
                            canEdit
                              ? t("Edit User")
                              : t("You don't have permission to edit user")
                          }
                          placement="top"
                        >
                          <button
                            disabled={!canEdit}
                            onClick={() => {
                              openEditUserModal(user);
                            }}
                            className="px-3 w-full py-2 border border-black text-black bg-white rounded-lg hover:bg-gray-700 hover:text-white transition-colors text-sm font-semibold flex items-center justify-center gap-1"
                          >
                            <Edit2 size={14} />
                            {/* {t("Edit")} */}
                          </button>
                        </Tooltip>
                        {/* <Tooltip
                          title={
                            canEdit
                              ? t("Edit Permission")
                              : t(
                                  "You don't have permission to edit permissions",
                                )
                          }
                          placement="top"
                        >
                          <button
                            disabled={!canEdit}
                            onClick={() => {
                              setEditingUser(user);
                              setPermissionsModal(true);
                            }}
                            className="px-3 w-full py-2 border border-[#0d223f] text-blue-600 bg-white rounded-lg hover:bg-[#0d223f] hover:text-white transition-colors text-sm font-semibold flex items-center justify-center gap-1"
                          >
                            <Lock size={14} />
                            {t("Permissions")}}
                          </button>
                        </Tooltip> */}

                        <Tooltip
                          title={
                            canDelete
                              ? t("Delete User")
                              : t("You don't have permission to delete user")
                          }
                          placement="top"
                        >
                          <button
                            disabled={!canDelete}
                            onClick={() => handleDeleteUser(user)}
                            className="px-3 w-full border border-red-600 py-2 text-red-600 bg-white rounded-lg hover:bg-red-700 hover:text-white transition-colors text-sm font-semibold flex items-center justify-center gap-1"
                          >
                            <Trash2 size={14} />
                            {/* {t("Delete")} */}
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.length === filtered.length}
                          onChange={selectAllUsers}
                          className="w-5 h-5 rounded border-gray-300 text-blue-600"
                        />
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">
                        {t("User")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">
                        {t("Contact")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">
                        {t("Role")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">
                        {t("Status")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">
                        {t("Created")}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">
                        {t("Actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filtered.map((user) => (
                      <tr
                        key={user.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            className="w-5 h-5 rounded border-gray-300 text-blue-600"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                              {user.profileImage ? (
                                <img
                                  src={user.profileImage}
                                  alt={user.id}
                                  className="w-full h-full object-cover"
                                />
                              ) : user.firstName || user.lastName ? (
                                <div>
                                  {user.firstName?.charAt(0)}
                                  {user.lastName?.charAt(0)}
                                </div>
                              ) : (
                                <UserIcon size={22} />
                              )}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900">
                                {user.firstName || user.lastName ? (
                                  <div>
                                    {user.firstName} {user.lastName}
                                  </div>
                                ) : (
                                  "No Name"
                                )}
                              </div>
                              {user.isEmailVerified && (
                                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full">
                                  {t("Verified")}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {
                              <div className="flex flex-row gap-1">
                                <ContactInfo
                                  type="email"
                                  value={user.email || "-"}
                                  icon={<Mail size={14} />}
                                />
                                {/* {!!user.email &&
                                  showVerification(user.isEmailVerified)} */}
                              </div>
                            }
                            {
                              <div className="flex flex-row gap-1">
                                <ContactInfo
                                  type="phone"
                                  value={
                                    (user?.phoneCode || "") +
                                    (user.phone || "-")
                                  }
                                  icon={<Phone size={14} />}
                                />
                                {/* {!!user.phone &&
                                  showVerification(user.isPhoneVerified)} */}
                              </div>
                            }
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex font-bold items-center gap-1 px-3 py-1 rounded-full text-md font-semibold ${user.role === "admin" ? "bg-green-100 text-green-800" : "bg-purple-100 text-purple-800"}`}
                          >
                            <Shield size={18} />
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleToggleStatus(user)}
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold transition-colors ${
                              user.isActive
                                ? "bg-green-100 text-green-800 hover:bg-green-200"
                                : "bg-red-100 text-red-800 hover:bg-red-200"
                            }`}
                          >
                            {user.isActive ? (
                              <>
                                <CheckCircle size={14} />
                                {t("Active")}
                              </>
                            ) : (
                              <>
                                <XCircle size={14} />
                                {t("Inactive")}
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Calendar size={14} />
                            {formatDate(user.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                openEditUserModal(user);
                              }}
                              className="p-2 border border-black text-black bg-white rounded-lg hover:text-white hover:bg-gray-700 transition-colors"
                              title={t("Edit")}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setEditingUser(user);
                                setPermissionsModal(true);
                              }}
                              className="p-2 border border-[#0d223f] text-blue-600 bg-white rounded-lg hover:text-white hover:bg-[#0d223f] transition-colors"
                              title={t("Permissions")}
                            >
                              <Lock size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="p-2 border border-red-600 text-red-600 bg-white rounded-lg hover:text-white hover:bg-red-700 transition-colors"
                              title={t("Delete")}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {finalTotalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={finalTotalPages}
              totalItems={finalTotalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              showPageSizeSelector={true}
              isLoading={loading}
              compact={true}
            />
          )}
        </div>
      </div>

      {/* Create User Modal */}
      {createModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setCreateModal(false);
            resetCreateForm();
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-black to-gray-700 px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <UserPlus className="w-6 h-6" />
                {t("Create New User")}
              </h3>
              <button
                onClick={() => {
                  setCreateModal(false);
                  resetCreateForm();
                }}
                className="text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("First Name")} *
                  </label>
                  <input
                    type="text"
                    value={createFormData.firstName}
                    onChange={(e) =>
                      setCreateFormData({
                        ...createFormData,
                        firstName: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Last Name")} *
                  </label>
                  <input
                    type="text"
                    value={createFormData.lastName}
                    onChange={(e) =>
                      setCreateFormData({
                        ...createFormData,
                        lastName: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Email")} ({t("Optional")})
                </label>
                <input
                  type="email"
                  value={createFormData.email}
                  onChange={(e) =>
                    setCreateFormData({
                      ...createFormData,
                      email: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Phone")} *
                </label>
                <div className="flex gap-2">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setShowCountryDropdown(!showCountryDropdown)
                      }
                      className="h-full flex items-center gap-2 px-3 py-3 border-2 border-gray-200 rounded-lg hover:border-gray-300 focus:border-black focus:outline-none bg-white"
                    >
                      <span className="text-xl">
                        {
                          COUNTRY_CODES.find(
                            (c) => c.code === createFormData.phoneCode,
                          )?.flag
                        }
                      </span>
                      <span className="font-semibold">
                        {createFormData.phoneCode}
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          showCountryDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {showCountryDropdown && (
                      <>
                        {/* Click-away layer */}
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowCountryDropdown(false)}
                        />
                        <div className="absolute top-full left-0 mt-2 w-72 bg-white border-2 border-gray-200 rounded-xl shadow-2xl z-20 overflow-hidden">
                          <div className="p-3 border-b border-gray-200">
                            <input
                              type="text"
                              autoFocus
                              placeholder={t("Search country...")}
                              value={searchCountry}
                              onChange={(e) => setSearchCountry(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-black focus:outline-none"
                            />
                          </div>
                          <div className="overflow-y-auto max-h-60">
                            {COUNTRY_CODES.filter(
                              (country) =>
                                country.name
                                  .toLowerCase()
                                  .includes(searchCountry.toLowerCase()) ||
                                country.code.includes(searchCountry) ||
                                country.country
                                  .toLowerCase()
                                  .includes(searchCountry.toLowerCase()),
                            ).map((country) => (
                              <button
                                key={country.code}
                                type="button"
                                onClick={() => {
                                  setCreateFormData({
                                    ...createFormData,
                                    phoneCode: country.code,
                                  });
                                  setShowCountryDropdown(false);
                                  setSearchCountry("");
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                              >
                                <span className="text-xl">{country.flag}</span>
                                <span className="font-medium flex-1">
                                  {country.name}
                                </span>
                                <span className="text-gray-500 font-semibold">
                                  {country.code}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    type="tel"
                    value={createFormData.phone}
                    onChange={(e) =>
                      setCreateFormData({
                        ...createFormData,
                        phone: e.target.value.replace(/\D/g, ""),
                      })
                    }
                    placeholder={t("Enter mobile number")}
                    maxLength={15}
                    className="flex-1 min-w-0 px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Role")} *
                </label>
                <select
                  value={createFormData.role}
                  onChange={(e) =>
                    setCreateFormData({
                      ...createFormData,
                      role: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none"
                >
                  <option value="">{t("Select Role")}</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t("Status")}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() =>
                      setCreateFormData({ ...createFormData, isActive: true })
                    }
                    className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                      createFormData.isActive
                        ? "bg-green-50 border-green-500 text-green-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <CheckCircle className="inline mr-2" size={16} />
                    {t("Active")}
                  </button>
                  <button
                    onClick={() =>
                      setCreateFormData({ ...createFormData, isActive: false })
                    }
                    className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                      !createFormData.isActive
                        ? "bg-red-50 border-red-500 text-red-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <XCircle className="inline mr-2" size={16} />
                    {t("Inactive")}
                  </button>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => {
                  setCreateModal(false);
                  resetCreateForm();
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={() => handleCreateUser()}
                disabled={saving}
                className="flex-1 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Creating...")}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {t("Create User")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModal && editingUser && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setEditModal(false);
            setEditingUser(null);
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-black to-gray-700 px-4 sm:px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <UserCog className="w-5 h-5 sm:w-6 sm:h-6" />
                {t("Edit User")}
              </h3>
              <button
                onClick={() => {
                  setEditModal(false);
                  setEditingUser(null);
                }}
                className="text-white hover:text-gray-300"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* User Info */}
              <div className="flex items-center gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                  {editingUser.profileImage ? (
                    <img
                      src={editingUser.profileImage}
                      alt={editingUser.id}
                      className="w-full h-full object-cover"
                    />
                  ) : editingUser.firstName || editingUser.lastName ? (
                    <div>
                      {editingUser.firstName?.charAt(0)}
                      {editingUser.lastName?.charAt(0)}
                    </div>
                  ) : (
                    <UserIcon size={22} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 truncate text-sm sm:text-base">
                    {editingUser.firstName} {editingUser.lastName}
                  </div>
                  {editingUser.email && (
                    <ContactInfo
                      type="email"
                      value={editingUser.email}
                      icon={<Mail size={16} />}
                    />
                  )}
                  {editingUser.phone && (
                    <ContactInfo
                      type="phone"
                      value={editingUser.phone}
                      icon={<Phone size={16} />}
                    />
                  )}
                </div>
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("User Role")}
                </label>
                <select
                  value={editingUserMatchedRole?.id || ""}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      roleId: e.target.value,
                      role:
                        roles.find((role) => role.id === e.target.value)?.name ||
                        editingUser.role,
                    })
                  }
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm sm:text-base"
                >
                  {!editingUserMatchedRole && editingUser.role && (
                    <option value="">{editingUser.role}</option>
                  )}
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Toggle */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t("Account Status")}
                </label>
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <button
                    onClick={() =>
                      setEditingUser({ ...editingUser, isActive: true })
                    }
                    className={`py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg border-2 font-semibold transition-all text-sm sm:text-base ${
                      editingUser.isActive
                        ? "bg-green-50 border-green-500 text-green-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <CheckCircle className="inline mr-1 sm:mr-2" size={16} />
                    {t("Active")}
                  </button>
                  <button
                    onClick={() =>
                      setEditingUser({ ...editingUser, isActive: false })
                    }
                    className={`py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg border-2 font-semibold transition-all text-sm sm:text-base ${
                      !editingUser.isActive
                        ? "bg-red-50 border-red-500 text-red-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <XCircle className="inline mr-1 sm:mr-2" size={16} />
                    {t("Inactive")}
                  </button>
                </div>
              </div>

              {/* Additional Info */}
              <div className="text-xs sm:text-sm text-gray-600">
                {/* <div className="border-t py-4 space-y-1 flex justify-between">
                  <span>{t("Email Verified")}:</span>
                  <span
                    className={`font-semibold ${
                      editingUser.isEmailVerified
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {editingUser.isEmailVerified ? "Yes" : "No"}
                  </span>
                </div> */}

                <div className="border-y py-4 space-y-1 flex justify-between">
                  <span>{t("Last Login")}:</span>
                  <span className="font-semibold">
                    {editingUser.lastLogin
                      ? formatDate(editingUser.lastLogin)
                      : "Never"}
                  </span>
                </div>
              </div>
            </div>

            <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setEditModal(false);
                  setEditingUser(null);
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-300 font-semibold text-sm sm:text-base"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={() => handleUpdateUser()}
                disabled={saving}
                className="flex-1 bg-black text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-gray-700 font-semibold disabled:opacity-70 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                {saving ? (
                  <>
                    <div
                      className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Saving...")}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 sm:w-5 sm:h-5" />
                    {t("Save Changes")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {profileModal && currentProfile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setProfileModal(false)}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-black px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <UserCog className="w-6 h-6" />
                {t("My Profile")}
              </h3>
              <button
                onClick={() => setProfileModal(false)}
                className="text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl">
                  {currentProfile.profileImage ? (
                    <img
                      src={currentProfile.profileImage}
                      alt={currentProfile.id}
                      className="w-full h-full object-cover"
                    />
                  ) : currentProfile.firstName || currentProfile.lastName ? (
                    <div>
                      {currentProfile.firstName?.charAt(0)}
                      {currentProfile.lastName?.charAt(0)}
                    </div>
                  ) : (
                    <UserIcon size={22} />
                  )}
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">
                    {currentProfile.firstName || currentProfile.lastName ? (
                      <div>
                        {currentProfile.firstName} {currentProfile.lastName}
                      </div>
                    ) : (
                      "No Name"
                    )}
                  </div>
                  <div
                    className={`inline-flex font-bold items-center gap-1 px-3 py-1 rounded-full text-md font-semibold ${currentProfile.role === "admin" ? "bg-green-100 text-green-800" : "bg-purple-100 text-purple-800"}`}
                  >
                    {/* <div className="text-md font-bold text-gray-600 flex items-center gap-2"> */}
                    <Shield size={18} />
                    {currentProfile.role}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("First Name")}
                  </label>
                  <input
                    type="text"
                    value={currentProfile.firstName}
                    onChange={(e) =>
                      setCurrentProfile({
                        ...currentProfile,
                        firstName: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Last Name")}
                  </label>
                  <input
                    type="text"
                    value={currentProfile.lastName}
                    onChange={(e) =>
                      setCurrentProfile({
                        ...currentProfile,
                        lastName: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Email")}
                </label>
                <input
                  type="email"
                  value={currentProfile.email}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-gray-50 text-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Phone")}
                </label>
                <input
                  type="tel"
                  value={currentProfile.phone || ""}
                  onChange={(e) =>
                    setCurrentProfile({
                      ...currentProfile,
                      phone: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                />
              </div>
              {/* We don't need this currently  */}
              {/* <button
                onClick={() => setPreferencesModal(true)}
                className="w-full px-4 py-3 bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 font-semibold flex items-center justify-center gap-2"
              >
                <Settings size={18} />
                {t("Edit Preferences")}
              </button> */}
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setProfileModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={() => handleUpdateProfile()}
                disabled={saving}
                className="flex-1 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Saving...")}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {t("Save Changes")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {preferencesModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setPreferencesModal(false)}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Settings className="w-6 h-6" />
                {t("User Preferences")}
              </h3>
              <button
                onClick={() => setPreferencesModal(false)}
                className="text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Language")}
                </label>
                <select
                  value={preferencesData.language}
                  onChange={(e) =>
                    setPreferencesData({
                      ...preferencesData,
                      language: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-purple-600 focus:outline-none"
                >
                  <option value="en">English</option>
                  <option value="ar">Arabic</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Theme")}
                </label>
                <select
                  value={preferencesData.theme}
                  onChange={(e) =>
                    setPreferencesData({
                      ...preferencesData,
                      theme: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-purple-600 focus:outline-none"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Auto</option>
                </select>
              </div>

              <div className="space-y-3">
                <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                  <span className="text-sm font-semibold text-gray-700">
                    {t("Enable Notifications")}
                  </span>
                  <input
                    type="checkbox"
                    checked={preferencesData.notifications}
                    onChange={(e) =>
                      setPreferencesData({
                        ...preferencesData,
                        notifications: e.target.checked,
                      })
                    }
                    className="w-5 h-5 rounded border-gray-300 text-purple-600"
                  />
                </label>

                <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                  <span className="text-sm font-semibold text-gray-700">
                    {t("Email Updates")}
                  </span>
                  <input
                    type="checkbox"
                    checked={preferencesData.emailUpdates}
                    onChange={(e) =>
                      setPreferencesData({
                        ...preferencesData,
                        emailUpdates: e.target.checked,
                      })
                    }
                    className="w-5 h-5 rounded border-gray-300 text-purple-600"
                  />
                </label>
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setPreferencesModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={() => handleUpdatePreferences()}
                disabled={saving}
                className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Saving...")}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {t("Save Preferences")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {permissionsModal && editingUser && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setPermissionsModal(false);
            setEditingUser(null);
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-black px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Lock className="w-6 h-6" />
                {t("Custom Permissions")} - {editingUser.firstName}{" "}
                {editingUser.lastName}
              </h3>
              <button
                onClick={() => {
                  setPermissionsModal(false);
                  setEditingUser(null);
                }}
                className="text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-900">
                  {t(
                    "These permissions override the default role permissions for this user.",
                  )}
                </p>
              </div>

              <div className="space-y-2">
                {allPermissions.length
                  ? allPermissions.map((permission) => (
                      <label
                        key={permission.id}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border-2 border-gray-200"
                      >
                        <input
                          type="checkbox"
                          checked={customPermissions.includes(permission.id)}
                          onChange={() => {
                            setCustomPermissions((prev) =>
                              prev.includes(permission.id)
                                ? prev.filter((p) => p !== permission.id)
                                : [...prev, permission.id],
                            );
                          }}
                          className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">
                            {permission.name}
                          </div>
                          <div className="text-sm text-gray-600">
                            {permission.description}
                          </div>
                        </div>
                      </label>
                    ))
                  : t("No permissions")}
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => {
                  setPermissionsModal(false);
                  setEditingUser(null);
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={() => handleUpdateCustomPermissions()}
                disabled={saving}
                className="flex-1 bg-black text-white px-6 py-3 rounded-lg hover:bg-[#0d223f] font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Saving...")}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {t("Update Permissions")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {statsModal && userStats && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setStatsModal(false)}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-green-600 to-green-800 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="w-6 h-6" />
                {t("User Statistics")}
              </h3>
              <button
                onClick={() => setStatsModal(true)}
                className="text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <Users className="w-8 h-8 text-blue-600" />
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-3xl font-bold text-blue-900">
                    {userStats.totalUsers}
                  </div>
                  <div className="text-sm text-blue-700 font-semibold">
                    {t("Total Users")}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="text-3xl font-bold text-green-900">
                    {userStats.activeUsers}
                  </div>
                  <div className="text-sm text-green-700 font-semibold">
                    {t("Active Users")}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <XCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <div className="text-3xl font-bold text-red-900">
                    {userStats.inactiveUsers}
                  </div>
                  <div className="text-sm text-red-700 font-semibold">
                    {t("Inactive Users")}
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <h4 className="font-bold text-purple-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {t("Users by Role")}
                </h4>
                <div className="space-y-3">
                  {userStats.usersByRole.map((roleData) => (
                    <div
                      key={roleData.role}
                      className="flex items-center justify-between p-3 bg-white rounded-lg"
                    >
                      <span className="font-semibold text-gray-900">
                        {roleData.role}
                      </span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full font-bold">
                        {roleData.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-6 pb-6">
              <button
                onClick={() => setStatsModal(false)}
                className="w-full bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("Close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {bulkUpdateModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setBulkUpdateModal(false)}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Upload className="w-6 h-6" />
                {t("Bulk Update Users")}
              </h3>
              <button
                onClick={() => setBulkUpdateModal(false)}
                className="text-white hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-100 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-blue-900">
                  {t("Updating")} {selectedUsers.length} {t("selected user(s)")}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("New Role")} ({t("optional")})
                </label>
                <select
                  value={bulkUpdateData.role}
                  onChange={(e) =>
                    setBulkUpdateData({
                      ...bulkUpdateData,
                      role: e.target.value,
                    })
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#0d223f] focus:outline-none"
                >
                  <option value="">{t("Keep current role")}</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t("Status")}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() =>
                      setBulkUpdateData({ ...bulkUpdateData, isActive: true })
                    }
                    className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                      bulkUpdateData.isActive
                        ? "bg-green-50 border-green-500 text-green-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <CheckCircle className="inline mr-2" size={16} />
                    {t("Active")}
                  </button>
                  <button
                    onClick={() =>
                      setBulkUpdateData({ ...bulkUpdateData, isActive: false })
                    }
                    className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                      !bulkUpdateData.isActive
                        ? "bg-red-50 border-red-500 text-red-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <XCircle className="inline mr-2" size={16} />
                    {t("Inactive")}
                  </button>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setBulkUpdateModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={() => handleBulkUpdate()}
                disabled={saving}
                className="flex-1 bg-[#0d223f] text-white px-6 py-3 rounded-lg hover:bg-[#0d223f] font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    <span>{t("Updating...")}</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    {t("Update Users")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        icon={Trash2}
        iconColor="text-red-600"
        iconBg="bg-red-100"
        onConfirm={confirmConfig.onConfirm}
        onCancel={closeConfirmModal}
      />
    </div>
  );
};

export default UsersManagementPage;
